<template>
  <lsm-widget
    :bg-image="bgImage"
    :text="text"
    text-color="primary-lighten-1"
    data-testid="user-profile-widget"
  >
    <template
      v-if="title"
      #warning
    >
      <div>
        <v-icon
          icon="fas fa-triangle-exclamation"
          class="text-warning"
        />
        <span class="text-headline-medium text-primary-darken-1"> {{ title }}</span>
      </div>
    </template>
    <template #buttons>
      <v-btn
        v-if="secondaryLink"
        :to="secondaryLink.to"
        color="secondary"
        data-testid="secondary-link"
      >
        {{ secondaryLink.caption }}
      </v-btn>
      <v-btn
        v-if="primaryLink"
        :to="primaryLink.to"
        color="primary"
        data-testid="primary-link"
      >
        {{ primaryLink.caption }}
      </v-btn>
    </template>
  </lsm-widget>
</template>

<script setup lang="ts">
import type { PropType } from 'vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuth, usePermissions } from '@/composables';
import LsmWidget from './LsmWidget.vue';

const props = defineProps({
  bgImage: {
    type: String,
  },
  profileImageStatus: {
    type: String as PropType<'required' | 'pending' | 'rejected' | 'complete'>,
  },
});

const { t } = useI18n();
const { authUser } = useAuth();
const { can } = usePermissions();

interface LinkType {
  caption: string;
  to: string;
}

const title = computed<string | undefined>(() => {
  switch (props.profileImageStatus) {
    case 'required':
      return t('images.missing_profile');
    case 'pending':
      return t('images.under_review');
    case 'rejected':
      return t('images.rejected');
    default: return undefined;
  }
});

const text = computed<string | undefined>(() => {
  switch (props.profileImageStatus) {
    case 'required':
      return t('images.missing_profile_message');
    case 'pending':
      return t('images.under_review_message');
    case 'rejected':
      return t('images.rejected_message');
    default: return undefined;
  }
});

const primaryLink = computed<LinkType | null>(() => {
  if (['required', 'rejected'].includes(props.profileImageStatus ?? '')) {
    return {
      caption: t('global.view_profile'),
      to: '/mydetails',
    };
  }

  if (can('submit_availability')) {
    return {
      caption: t('smpw.submit_availability'),
      to: '/mycalendar',
    };
  }
  if (authUser.is_admin) {
    return {
      caption: t('global.administration'),
      to: '/admin',
    };
  }
  return null;
});

const secondaryLink = computed(() => {
  if (['required', 'rejected'].includes(props.profileImageStatus ?? '')) {
    return null;
  }
  if (can('submit_availability')) {
    return {
      caption: t('shift.manage'),
      to: '/myshifts',
    };
  }
  return {
    caption: t('global.my_details'),
    to: '/mydetails',
  };
});
</script>
